import { AccountType } from '@/data/usecases/account/account-type'
import { Account } from '@/domain/usecases'
import { AccountProfessional } from '@/domain/usecases/account/account-professional'
import { SkillModel } from '@/domain/models/onboarding/user-onboarding-model'
import { LearningGoal } from '@/domain/models/onboarding/learning-goal'
import { WeeklyDedication } from '@/domain/models/onboarding/weekly-dedication'
import { UserOnboarding } from '@/domain/usecases/onboarding/user-onboarding'
import { UserProfileAssessment } from '@/domain/usecases/profile-assessment/user-profile-assessment'
import { getWeakness } from '@/domain/models/profile-assessment/user-profile-assessment-model'
import { ContentRecommendedListModel } from '@/domain/models/content/user-content-recommended-model'
import { UserContentRecommended } from '@/domain/usecases/content/user-content-recommended'
import { ContentRecommendationType } from '@/domain/models/content/content-recommendation-type'
import { UserContentSummary } from '@/domain/usecases/content/user-content-summary'
import { UserEnrollmentListItemModel } from '@/domain/models/content/enrollment/user-enrollment-list-model'
import { UserEnrollmentList } from '@/domain/usecases/content/enrollment/user-enrollment-list'
import { queryClient } from '@/infra/http'
import { importRemote } from '@/main/routes/helpers/loaders'
import { checkFlagEnabled, FeatureFlagName } from '@/infra/feature-flag'
import {
  EMPTY_LEARNING_PREFERENCES,
  LEARNING_PREFERENCES_QUERY_KEY,
  learningPreferencesService,
  LearningPreferencesModel
} from '@/data/services/user/learning-preferences-service'

export type RecommendationItem = {
  type: ContentRecommendationType.Enum
  correlationId: number
  contents: ContentRecommendedListModel
}

export type UserSpaceLoaderDataType = {
  profileAssessment: UserProfileAssessment.Model | null
  accountProfessional?: AccountProfessional.Model
  skills: SkillModel[]
  /** Legado — consumido pelo `ObjectiveForm` quando a flag de preferências está off. */
  learningGoal: LearningGoal.Enum | null
  /**
   * Legado, em DIAS — enum de frequência do onboarding.
   *
   * Nome idêntico ao `learningPreferences.weeklyDedication` logo abaixo, que é em HORAS: os dois
   * campos chegam ao Meu Espaço e só a origem os distingue. Este sai quando a flag de
   * preferências (`ENABLE_USER_LEARNING_PREFERENCES`) virar padrão e o `ObjectiveForm` morrer.
   */
  weeklyDedication: WeeklyDedication.Enum | null
  recommendations: RecommendationItem[]
  enrollments: UserEnrollmentListItemModel[]
  learningPreferences: LearningPreferencesModel | null
}

const MAX_RECOMMENDATION_ITEMS = 3

/**
 * Loads user space data including profile assessment, skills, recommendations, and enrollments
 * Handles both assessment-based and skill-based content recommendations
 */
export async function loadUserSpace(account: Account.Model): Promise<UserSpaceLoaderDataType> {
  const isDependent = AccountType.Enum.DEPENDENT.is(account)

  // Load all required data in parallel
  const [
    remoteUserProfileAssessment,
    remoteUserOnboarding,
    remoteUserEnrollmentList,
    remoteAccountProfessional
  ] = await Promise.all([
    importRemote<UserProfileAssessment>('makeRemoteUserProfileAssessment', queryClient),
    importRemote<UserOnboarding>('makeRemoteUserOnboarding', queryClient),
    importRemote<UserEnrollmentList>('makeRemoteUserEnrollmentList', queryClient),
    isDependent
      ? Promise.resolve(undefined)
      : importRemote<AccountProfessional>('makeRemoteAccountProfessional', queryClient)
  ])

  const enableUserLearningPreferences = checkFlagEnabled(
    FeatureFlagName.ENABLE_USER_LEARNING_PREFERENCES
  )

  const learningPreferencesPromise = enableUserLearningPreferences
    ? queryClient
        .fetchQuery({
          queryKey: [LEARNING_PREFERENCES_QUERY_KEY],
          queryFn: () => learningPreferencesService.fetch(),
          staleTime: 0
        })
        .catch(() => EMPTY_LEARNING_PREFERENCES)
    : Promise.resolve(null)

  const [
    profileAssessment,
    onboardingModel,
    enrollments,
    accountProfessional,
    learningPreferences
  ] = await Promise.all([
    remoteUserProfileAssessment.getLatest(),
    remoteUserOnboarding.get(),
    remoteUserEnrollmentList.get(),
    remoteAccountProfessional ? remoteAccountProfessional.get(account.id) : undefined,
    learningPreferencesPromise
  ])

  // Get recommendations based on assessment and skills
  const recommendations = await getRecommendedContent(profileAssessment)

  return {
    profileAssessment,
    accountProfessional,
    enrollments,
    skills: onboardingModel.interest.skills,
    learningGoal: onboardingModel.learningGoal,
    weeklyDedication: onboardingModel.weeklyDedication,
    recommendations,
    learningPreferences
  }
}

/**
 * Get content recommendations from both assessment competences and skills
 */
async function getRecommendedContent(
  assessment: UserProfileAssessment.Model | null
): Promise<RecommendationItem[]> {
  const [assessmentRecommendations, skillRecommendations] = await Promise.all([
    getRecommendedContentByAssessment(assessment),
    getRecommendedContentBySkills()
  ])

  return [...assessmentRecommendations, ...skillRecommendations]
}

/**
 * Get content recommendations based on assessment weakness
 */
async function getRecommendedContentByAssessment(
  assessment: UserProfileAssessment.Model | null
): Promise<RecommendationItem[]> {
  if (!assessment?.competences?.length) {
    return []
  }

  const weakness = getWeakness(assessment)
  const remoteUserContentRecommended = await importRemote<UserContentRecommended>(
    'makeRemoteUserContentRecommended',
    queryClient
  )

  return Promise.all(
    weakness.map(item =>
      remoteUserContentRecommended
        .get(
          ContentRecommendationType.Enum.ASSESSMENT_COMPETENCE,
          MAX_RECOMMENDATION_ITEMS,
          item.competence.id
        )
        .then(data => ({
          type: ContentRecommendationType.Enum.ASSESSMENT_COMPETENCE,
          correlationId: item.competence.id,
          contents: data
        }))
    )
  )
}

/**
 * Get content recommendations based on user skills
 */
async function getRecommendedContentBySkills(): Promise<RecommendationItem[]> {
  const remoteUserContentSummary = await importRemote<UserContentSummary>(
    'makeRemoteUserContentSummary',
    queryClient
  )

  const data = await remoteUserContentSummary.get()

  return data.skills.map(skill => ({
    type: ContentRecommendationType.Enum.SKILL,
    correlationId: skill.id,
    contents: skill.items.slice(0, MAX_RECOMMENDATION_ITEMS).map((content, i) => ({
      ...content,
      sortOrder: i
    }))
  }))
}
